/* @flow */

import type {
  Product,
  Category,
  CmsPage,
  AffiliateSingle,
  StoreConfiguration,
  CurrentInfoItem,
  InfoBlock } from "shop-state/types";
import type { PointsPrice } from "helpers/points";

import { useContext } from "react";
import { StoreInfoContext } from "entrypoint/shared";
import { decodeHTML } from "entities";
import striptags from "striptags";
import { useLocation } from "react-router";

type Meta = {
  title: string,
  description?: string,
  canonicals?: Array<{
    rel: string,
    href: string,
  }>,
  og?: {
    title: string,
    description: string,
    type?: string,
    url?: string,
    image?: string,
  },
  twitter?: {},
  product?: {},
  price?: {},
};

type MetaReponse = {
  title: string,
  link: Array<{
    rel: string,
    href: string,
  }>,
  data: Array<{ [key: string]: ?string }>,
};

type MetaLink = { rel: string, type: string, href: string };

const ID = x => x;

export const formatMeta = ({
  title,
  description,
  canonicals,
  og = {},
  twitter = {},
  product = {},
  price = {},
}: Meta): MetaReponse => {
  const meta = {
    title,
    data: [],
    link: [],
  };

  const openGraph = {
    title,
    description,
    ...og,
  };

  Object.keys(openGraph).filter(k => openGraph[k]).forEach(k => {
    meta.data.push({ property: `og:${k}`, content: openGraph[k] });
  });
  Object.keys(twitter).filter(k => twitter[k]).forEach(k => {
    meta.data.push({ property: `twitter:${k}`, content: twitter[k] });
  });
  Object.keys(product).filter(k => product[k]).forEach(k => {
    meta.data.push({ property: `product:${k}`, content: product[k] });
  });
  Object.keys(price).filter(k => price[k]).forEach(k => {
    meta.data.push({ property: `product:price:${k}`, content: price[k] });
  });

  if (description) {
    meta.data.push({ name: "description", content: description });
  }

  if (canonicals) {
    canonicals.forEach(a => {
      meta.link.push({
        rel: "canonical",
        href: a.href,
      });
    });
  }

  return meta;
};

export const getCategoryMeta = (category: Category): {|
  title: string,
  data: Array<{|
      content: string, name: string,
    |} | {|
      content: string, property: string,
    |}>,
|} => {
  const title = category.metaTitle || category.name;
  const description = category.metaDescription || category.description;

  const meta = {
    title,
    data: [{ property: "og:title", content: title }],
  };

  if (description) {
    meta.data.push(
      { name: "description", content: description },
      { property: "og:description", content: description }
    );
  }

  return meta;
};

export const useGetFrontPageMeta = (title: string, description: string): MetaReponse => {
  const { info } = useContext(StoreInfoContext);

  return formatMeta({
    title,
    description,
    canonicals: [{ rel: "canonical", href: info.baseUrl }],
    og: {
      title,
      description,
    },
  });
};

export const useGetProductMeta = (
  product: Product,
  t: string => string,
  pointsPrice?: ?PointsPrice
): MetaReponse => {
  const { info } = useContext(StoreInfoContext);
  if (!product) {
    return {};
  }

  const description = striptags(decodeHTML(
    product.attributes.metaDescription ||
    product.attributes.shortDescription
  ));

  const title = product.attributes.metaTitle || [product.name, product.attributes.manufacturer].filter(ID).join(", ");

  const { smallImage, manufacturer } = product.attributes;
  const url = `${info.baseUrl}${product.url.slice(1)}`; // No relative urls
  const priceAmount = pointsPrice ? pointsPrice.points.value.exVat : product.price.incVat;
  const priceCurrency = t("LOCALE.POINTS");
  const availability = "in stock";

  return formatMeta({
    title,
    description,
    canonicals: [{ rel: "canonical", href: url }],
    og: {
      type: "product",
      title,
      url,
      description,
      image: smallImage?.x1,
    },
    twitter: {
      card: "product",
      title,
      description,
      image: smallImage?.x1,
    },
    product: {
      brand: manufacturer,
      availability,
    },
    price: {
      amount: priceAmount,
      currency: priceCurrency,
    },
  });
};

export const useGetCmsMeta = (page: CmsPage): MetaReponse => {
  const { title, metaDescription: description } = page;
  const { info } = useContext(StoreInfoContext);
  return formatMeta({
    title,
    description,
    canonicals: [{ rel: "canonical", href: `${info.baseUrl}${page.url.slice(1)}` }],
    og: {
      title,
      description,
    },
  });
};

export const useGetAffiliateMeta = (affiliate: ?AffiliateSingle): null | MetaReponse => {
  const { info } = useContext(StoreInfoContext);
  const location = useLocation();

  if (!affiliate) {
    return {};
  }

  const description = affiliate.description || "";

  let link = "";
  const firstLink = typeof affiliate.links !== "undefined" && affiliate.links ? affiliate.links[0] : null;

  if (typeof firstLink === "undefined" || firstLink === null) {
    return null;
  }

  link = firstLink.linkText;

  const { title } = affiliate;

  const image = affiliate.image ? `${info.baseUrl}${affiliate.image.slice(1)}` : "";
  const url = `${info.baseUrl}${location.pathname.slice(1)}`;

  if (!title || !link) {
    return null;
  }

  return formatMeta({
    title,
    description,
    canonicals: [{ rel: "canonical", href: link }],
    og: {
      title,
      description,
      url,
      image,
    },
  });
};

export const getFavicons = (configuration: StoreConfiguration): Array<MetaLink> => {
  const links = [];

  if (configuration.faviconICO) {
    links.push({ rel: "icon", type: "image/vnd.microsoft.icon", href: configuration.faviconICO });
  }

  if (configuration.favicon16) {
    links.push({ rel: "icon", type: "image/png", size: "16x16", href: configuration.favicon16 });
  }

  if (configuration.favicon32) {
    links.push({ rel: "icon", type: "image/png", size: "32x32", href: configuration.favicon32 });
  }

  if (configuration.favicon96) {
    links.push({ rel: "icon", type: "image/png", size: "96x96", href: configuration.favicon96 });
  }

  return links;
};

export const useGetNewsMeta = (item?: CurrentInfoItem | InfoBlock | null): MetaReponse | null => {
  const { info } = useContext(StoreInfoContext);
  const location = useLocation();

  if (!item) {
    return null;
  }

  const title = item.title ?? "";
  const description = item.content ? striptags(decodeHTML(
    item.content
  )) : "";
  const image = item.image ? `${info.baseUrl}${item.image.slice(1)}` : "";
  const url = `${info.baseUrl}${location.pathname.slice(1)}`;

  return formatMeta({
    title,
    description,
    og: {
      title,
      description,
      image,
      url,
    },
  });
};
